import { ICellRendererParams } from 'ag-grid-community';
import { Moment } from 'moment';
import React from 'react';
import styled from 'styled-components';
import { User } from 'api/models';
import {
  getCapacityInMinutesForADay,
  WorkingDaysConfiguration,
} from 'components/organisms/user-capacity';
import { fullName } from 'utils/full-name';
import { checkIfCurrentDateHasTimeOff } from 'utils/time-off-utils';
import { TimeBarCell } from '../../common/cells/time-cells';
import { FormatTime } from '../../common/format-time';
import {
  getCellClassesForTimesheetCell,
  isNonWorkingDay,
} from '../../common/utils';
import {
  TimesheetReminder,
  TimesheetStatus,
  UserLevelSummary,
} from '../../types';
import { ReminderCell } from '../reminder-cell';

const Value = styled.div`
  width: 100%;
  height: 100%;
  cursor: pointer;
`;

const TimeBar = styled(TimeBarCell)`
  position: unset;
`;

interface DateTimeEntryTypes {
  onUserSelection: (userId: number, projectId?: number) => void;
  sendReminder: (user: User, projectId?: number) => Promise<TimesheetReminder>;
  day: Moment;
  workingDays: WorkingDaysConfiguration;
  isManagerApproval: boolean;
  value: UserLevelSummary;
}

export const DayTimeEntryRenderer = (
  params: ICellRendererParams & DateTimeEntryTypes,
) => {
  const {
    value: timesheet,
    onUserSelection,
    sendReminder,
    day,
    api,
    workingDays,
    isManagerApproval,
  } = params;

  const recordValue: Record<string, number> = timesheet.days;

  const totalMinutes = Object.values(recordValue).reduce(
    (total, dayValue) => total + dayValue,
    0,
  );

  if (timesheet.status === TimesheetStatus.NotSubmitted) {
    return (
      <ReminderCell
        sendReminder={sendReminder}
        lastReminderAt={timesheet.lastReminderAt}
        totalMinutes={totalMinutes}
        timesheet={timesheet}
        api={api}
        onUserSelection={onUserSelection}
        isManagerApproval={isManagerApproval}
      />
    );
  }

  const isTimeOff = checkIfCurrentDateHasTimeOff(
    timesheet.user?.userTimeOffDetails,
    day,
  );

  if (isManagerApproval) {
    return (
      <TimeBar
        minutes={timesheet.days[day.format('YYYY-MM-DD')] ?? 0}
        capacity={
          !isNonWorkingDay(day, workingDays)
            ? getCapacityInMinutesForADay(timesheet.user.capacity, workingDays)
            : 0
        }
        isTimeOff={isTimeOff}
        userName={fullName(timesheet.user)}
        hideBackground={!!isTimeOff}
        onUserSelection={() => onUserSelection(timesheet.userId)}
      />
    );
  }

  return (
    <Value
      className={getCellClassesForTimesheetCell(day, timesheet)}
      onClick={() => onUserSelection(timesheet.userId, timesheet.projectId)}
    >
      <FormatTime
        value={timesheet.days[day.format('YYYY-MM-DD')] ?? 0}
        showHypenIfZero={false}
      />
    </Value>
  );
};
